/**
 * Community — admin moderation view of the resident wall.
 *
 * KPI strip (posts last 7d / pinned / hidden) + filter pills + feed list.
 * Per-row actions: View / Pin/Unpin / Hide/Unhide / Delete.
 * Click row → drawer with comments inline.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessagesSquare,
  Pin,
  PinOff,
  EyeOff,
  Eye,
  Trash2,
  MessageCircle,
  Heart,
  Calendar,
  Search,
  HelpCircle,
  Lightbulb,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';

type Category = 'general' | 'event' | 'lost_found' | 'recommendation' | 'question';
type Status = 'visible' | 'hidden' | 'deleted';

interface Author {
  id: string;
  role: 'resident' | 'admin';
  full_name: string;
  unit: string | null;
  block_name: string | null;
}

interface CommunityPost {
  id: string;
  society_id: string;
  author_id: string;
  author_role: 'resident' | 'admin';
  category: Category;
  body: string;
  image_url: string | null;
  status: Status;
  pinned: boolean;
  like_count: number;
  comment_count: number;
  hidden_by_id: string | null;
  hidden_reason: string | null;
  created_at: string;
  updated_at: string;
  author?: Author;
  liked_by_me?: boolean;
}

interface CommunityComment {
  id: string;
  society_id: string;
  post_id: string;
  author_id: string;
  author_role: 'resident' | 'admin';
  body: string;
  status: Status;
  created_at: string;
  author?: Author;
}

interface PostListResponse {
  items: CommunityPost[];
  next_cursor: string | null;
}
interface CommentListResponse {
  items: CommunityComment[];
  next_cursor: string | null;
}

const CATEGORY_LABEL: Record<Category, string> = {
  general: 'General',
  event: 'Event',
  lost_found: 'Lost & found',
  recommendation: 'Recommendation',
  question: 'Question'
};

const CATEGORY_ICON: Record<
  Category,
  React.ComponentType<React.SVGProps<SVGSVGElement> & { strokeWidth?: number | string }>
> = {
  general: MessagesSquare,
  event: Calendar,
  lost_found: Search,
  recommendation: Lightbulb,
  question: HelpCircle
};

const CATEGORY_VARIANT: Record<Category, 'info' | 'warn' | 'alert' | 'ok' | 'muted'> = {
  general: 'info',
  event: 'ok',
  lost_found: 'warn',
  recommendation: 'info',
  question: 'muted'
};

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'general', label: 'General' },
  { value: 'event', label: 'Event' },
  { value: 'lost_found', label: 'Lost & found' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'question', label: 'Question' }
];

const AUTHOR_ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'admin', label: 'Admin' },
  { value: 'resident', label: 'Resident' }
];

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDateTime(iso);
}

function authorInitials(a: Author | undefined): string {
  if (!a?.full_name) return 'M';
  const parts = a.full_name.split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
}

// URL-synced filter+sort+page state. `status_filter` keeps the
// visible-vs-hidden lens that the previous pill-bar exposed.
const COMMUNITY_FILTER_DEFAULTS = {
  search: '',
  category: '',
  is_pinned: 'all', // 'all' | 'on' | 'off'
  author_role: '',
  status_filter: 'visible', // 'visible' | 'hidden'
  sort: 'created_desc',
  page_size: 50
};

export function CommunityPage(): JSX.Element {
  const [filterState, setFilterState] = useUrlSearchState(COMMUNITY_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const [drillId, setDrillId] = useState<string | null>(null);
  const [hideTarget, setHideTarget] = useState<CommunityPost | null>(null);
  const [hideReason, setHideReason] = useState('');
  const [removePostTarget, setRemovePostTarget] = useState<CommunityPost | null>(null);
  const [removeCommentId, setRemoveCommentId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Debounce free-text search → URL state.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const showHidden = filterState.status_filter === 'hidden';

  const communityFiltersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.category,
        filterState.author_role,
        filterState.is_pinned,
        filterState.sort,
        filterState.page_size,
        filterState.status_filter
      ]),
    [filterState]
  );

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      limit: filterState.page_size,
      sort: filterState.sort,
      // Admin sees both visible+hidden when the lens flips to "hidden".
      include_hidden: true
    };
    if (filterState.search) params.q = filterState.search;
    if (filterState.category) params.category = filterState.category;
    if (filterState.author_role) params.author_role = filterState.author_role;
    if (filterState.is_pinned === 'on') params.is_pinned = true;
    if (filterState.is_pinned === 'off') params.is_pinned = false;
    return params;
  }, [filterState]);

  const [communityCursor, setCommunityCursor] = useState<string | null>(null);
  useEffect(() => {
    setCommunityCursor(null);
  }, [communityFiltersHash]);
  const list = useQuery({
    queryKey: ['community-posts', queryParams, communityCursor],
    queryFn: () =>
      get<PostListResponse>('/community/posts', {
        params: { ...queryParams, ...(communityCursor ? { cursor: communityCursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const communityPaginator = useCursorAccumulator<CommunityPost>(list.data, communityCursor, communityFiltersHash);

  const items = useMemo(() => {
    const all = communityPaginator.items;
    // The "Status" lens is the only client-side narrowing left — backend
    // returns both visible+hidden so admin can toggle without a refetch.
    if (showHidden) return all.filter((p) => p.status === 'hidden');
    return all.filter((p) => p.status === 'visible');
  }, [communityPaginator.items, showHidden]);

  // KPI strip: posts last 7d / pinned / hidden
  const kpi = useMemo(() => {
    const all = communityPaginator.items;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const last7 = all.filter((p) => new Date(p.created_at).getTime() >= sevenDaysAgo).length;
    const pinned = all.filter((p) => p.pinned).length;
    const hidden = all.filter((p) => p.status === 'hidden').length;
    return { last7, pinned, hidden };
  }, [communityPaginator.items]);

  // Sort + filter chip wiring for the shared toolbar.
  const sortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Most recent' },
      { value: 'most_reactions', label: 'Most reactions' },
      { value: 'most_comments', label: 'Most comments' }
    ],
    []
  );

  const filterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        value: filterState.category,
        options: CATEGORY_OPTIONS,
        onChange: (v) => setFilterState({ category: v })
      },
      {
        id: 'author_role',
        label: 'Author role',
        type: 'select',
        value: filterState.author_role,
        options: AUTHOR_ROLE_OPTIONS,
        onChange: (v) => setFilterState({ author_role: v })
      },
      {
        id: 'is_pinned',
        label: 'Pinned',
        type: 'toggle',
        value: filterState.is_pinned as 'all' | 'on' | 'off',
        onLabel: 'Pinned only',
        offLabel: 'Unpinned',
        onChange: (v) => setFilterState({ is_pinned: v })
      },
      {
        id: 'status_filter',
        label: 'Status',
        type: 'select',
        value: filterState.status_filter,
        options: [
          { value: 'visible', label: 'Visible' },
          { value: 'hidden', label: 'Hidden' }
        ],
        onChange: (v) => setFilterState({ status_filter: v })
      }
    ],
    [filterState, setFilterState]
  );

  const hasActiveFilters =
    filterState.search !== COMMUNITY_FILTER_DEFAULTS.search ||
    filterState.category !== COMMUNITY_FILTER_DEFAULTS.category ||
    filterState.is_pinned !== COMMUNITY_FILTER_DEFAULTS.is_pinned ||
    filterState.author_role !== COMMUNITY_FILTER_DEFAULTS.author_role ||
    filterState.status_filter !== COMMUNITY_FILTER_DEFAULTS.status_filter ||
    filterState.sort !== COMMUNITY_FILTER_DEFAULTS.sort ||
    filterState.page_size !== COMMUNITY_FILTER_DEFAULTS.page_size;

  const resetFilters = () => {
    setFilterState(COMMUNITY_FILTER_DEFAULTS);
    setSearchInput('');
  };

  const drillPost = items.find((p) => p.id === drillId) ?? list.data?.items.find((p) => p.id === drillId) ?? null;

  const commentsQuery = useQuery({
    queryKey: ['community-comments', drillId],
    queryFn: () =>
      drillId
        ? get<CommentListResponse>(`/community/posts/${drillId}/comments`, { params: { limit: 100 } })
        : Promise.reject(new Error('no post')),
    enabled: Boolean(drillId)
  });

  const togglePin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      patch<CommunityPost>(`/community/posts/${id}`, { pinned }),
    onSuccess: (post) => {
      toast.success(post.pinned ? 'Post pinned' : 'Post unpinned');
      void queryClient.invalidateQueries({ queryKey: ['community-posts'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const hide = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<CommunityPost, { hidden_reason?: string }>(`/community/posts/${id}/hide`, {
        ...(reason ? { hidden_reason: reason } : {})
      }),
    onSuccess: () => {
      toast.success('Post hidden');
      void queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      setHideTarget(null);
      setHideReason('');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const unhide = useMutation({
    mutationFn: (id: string) => post<CommunityPost>(`/community/posts/${id}/unhide`, {}),
    onSuccess: () => {
      toast.success('Post restored');
      void queryClient.invalidateQueries({ queryKey: ['community-posts'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/community/posts/${id}`),
    onSuccess: () => {
      toast.success('Post deleted');
      void queryClient.invalidateQueries({ queryKey: ['community-posts'] });
      setDrillId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const removeComment = useMutation({
    mutationFn: (id: string) => del<void>(`/community/comments/${id}`),
    onSuccess: () => {
      toast.success('Comment deleted');
      void queryClient.invalidateQueries({ queryKey: ['community-comments', drillId] });
      void queryClient.invalidateQueries({ queryKey: ['community-posts'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <>
      <PageHeader
        title="Community"
        description="Moderate the resident wall. Pin announcements, hide off-topic posts, and remove abuse."
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-3 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-3">
          <KpiCard label="Posts (last 7d)" value={kpi.last7} icon={<Sparkles className="h-4 w-4" />} />
          <KpiCard label="Pinned" value={kpi.pinned} icon={<Pin className="h-4 w-4" />} />
          <KpiCard label="Hidden" value={kpi.hidden} icon={<EyeOff className="h-4 w-4" />} />
        </div>

        {/* Advanced filter toolbar */}
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search post body…"
          filters={filterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: sortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
          resultLabel={
            list.data ? `${items.length} result${items.length === 1 ? '' : 's'}` : 'Loading…'
          }
        />

        {/* Feed list */}
        {list.isLoading ? (
          <div className="grid gap-vt-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load community posts"
                description={(list.error as Error)?.message}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : items.length === 0 ? (
          <Card>
            <CardContent>
              {hasActiveFilters ? (
                <EmptyState
                  icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
                  title="No posts match your filters"
                  description="Try widening the filters or clear them to see the full wall."
                  action={
                    <Button variant="secondary" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
                  title={showHidden ? 'No hidden posts' : 'Nothing to moderate'}
                  description={
                    showHidden
                      ? 'Posts hidden by admin will appear here.'
                      : 'Residents have not posted yet.'
                  }
                />
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-vt-3">
            {items.map((p) => {
              const CategoryIcon = CATEGORY_ICON[p.category];
              return (
                <Card key={p.id} interactive>
                  <CardContent className="space-y-vt-3 pt-vt-4">
                    <div className="flex items-start justify-between gap-vt-3">
                      <div className="flex min-w-0 items-start gap-vt-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-primary text-vt-12 font-semibold uppercase">
                          {authorInitials(p.author)}
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-vt-2">
                            <span className="text-vt-14 font-semibold text-vt-text">
                              {p.author?.full_name ?? 'Member'}
                            </span>
                            {p.author?.role === 'admin' ? (
                              <Badge variant="info" className="text-[10px]">
                                Admin
                              </Badge>
                            ) : null}
                            {p.author?.unit ? (
                              <span className="text-vt-12 text-vt-text-muted">
                                {p.author.block_name ? `${p.author.block_name}-` : ''}
                                {p.author.unit}
                              </span>
                            ) : null}
                            <span className="text-vt-12 text-vt-text-soft">· {relativeTime(p.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-vt-1">
                        <Badge variant={CATEGORY_VARIANT[p.category]} className="flex items-center gap-1">
                          <CategoryIcon className="h-3 w-3" strokeWidth={2} />
                          {CATEGORY_LABEL[p.category]}
                        </Badge>
                        {p.pinned ? (
                          <Badge variant="warn" className="flex items-center gap-1">
                            <Pin className="h-3 w-3" strokeWidth={2} />
                            Pinned
                          </Badge>
                        ) : null}
                        {p.status === 'hidden' ? (
                          <Badge variant="alert" className="flex items-center gap-1">
                            <EyeOff className="h-3 w-3" strokeWidth={2} />
                            Hidden
                          </Badge>
                        ) : null}
                      </div>
                    </div>

                    <p className="whitespace-pre-line break-words text-vt-14 leading-relaxed text-vt-text">
                      {p.body}
                    </p>

                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt=""
                        className="max-h-72 w-full rounded-vt-md border border-vt-border object-cover"
                        loading="lazy"
                      />
                    ) : null}

                    <div className="flex flex-wrap items-center justify-between gap-vt-2 border-t border-vt-border pt-vt-3">
                      <div className="flex items-center gap-vt-4 text-vt-13 text-vt-text-muted">
                        <span className="inline-flex items-center gap-1">
                          <Heart className="h-4 w-4" strokeWidth={1.75} />
                          {p.like_count}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                          {p.comment_count}
                        </span>
                      </div>
                      <div className="flex items-center gap-vt-1">
                        <Button variant="ghost" size="sm" onClick={() => setDrillId(p.id)}>
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title={p.pinned ? 'Unpin' : 'Pin to top'}
                          disabled={togglePin.isPending}
                          onClick={() => togglePin.mutate({ id: p.id, pinned: !p.pinned })}
                        >
                          {p.pinned ? <PinOff className="h-4 w-4 text-vt-warn" /> : <Pin className="h-4 w-4 text-vt-text-soft" />}
                        </Button>
                        {p.status === 'hidden' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Restore (unhide)"
                            disabled={unhide.isPending}
                            onClick={() => unhide.mutate(p.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Hide post"
                            onClick={() => {
                              setHideTarget(p);
                              setHideReason('');
                            }}
                          >
                            <EyeOff className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-vt-text-soft"
                          title="Delete post"
                          disabled={remove.isPending}
                          onClick={() => setRemovePostTarget(p)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            <CursorPaginator
              hasMore={Boolean(communityPaginator.nextCursor)}
              loading={list.isFetching}
              itemsShown={items.length}
              pageSize={filterState.page_size}
              onLoadMore={() => {
                if (communityPaginator.nextCursor) setCommunityCursor(communityPaginator.nextCursor);
              }}
            />
          </div>
        )}
      </div>

      {/* Drill drawer */}
      <Dialog open={Boolean(drillId)} onOpenChange={(v) => (v ? null : setDrillId(null))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Post detail</DialogTitle>
            <DialogDescription>Comments are shown inline. Admin can remove any comment.</DialogDescription>
          </DialogHeader>
          {drillPost ? (
            <div className="space-y-vt-4">
              <Card>
                <CardContent className="space-y-vt-2 pt-vt-4">
                  <div className="flex items-center gap-vt-2 text-vt-13 text-vt-text-muted">
                    <span className="font-semibold text-vt-text">{drillPost.author?.full_name ?? 'Member'}</span>
                    {drillPost.author?.unit ? (
                      <span>
                        · {drillPost.author.block_name ? `${drillPost.author.block_name}-` : ''}
                        {drillPost.author.unit}
                      </span>
                    ) : null}
                    <span>· {relativeTime(drillPost.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-line break-words text-vt-14 text-vt-text">{drillPost.body}</p>
                  {drillPost.image_url ? (
                    <img src={drillPost.image_url} alt="" className="max-h-72 w-full rounded-vt-md border border-vt-border object-cover" />
                  ) : null}
                  {drillPost.status === 'hidden' ? (
                    <div className="rounded-vt-md border border-vt-alert/40 bg-vt-alert-100 px-vt-3 py-vt-2 text-vt-12 text-vt-alert">
                      Hidden by admin{drillPost.hidden_reason ? ` — ${drillPost.hidden_reason}` : ''}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <div>
                <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                  Comments ({drillPost.comment_count})
                </div>
                {commentsQuery.isLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : (commentsQuery.data?.items ?? []).length === 0 ? (
                  <div className="rounded-vt-md border border-dashed border-vt-border bg-vt-surface-2/40 px-vt-3 py-vt-4 text-center text-vt-12 text-vt-text-muted">
                    No comments yet.
                  </div>
                ) : (
                  <div className="space-y-vt-2">
                    {(commentsQuery.data?.items ?? []).map((c) => (
                      <div
                        key={c.id}
                        className={`rounded-vt-md border px-vt-3 py-vt-2 ${
                          c.status === 'deleted' ? 'border-dashed opacity-60' : 'border-vt-border'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-vt-2 text-vt-12 text-vt-text-muted">
                          <div className="flex items-center gap-vt-2">
                            <span className="font-semibold text-vt-text">{c.author?.full_name ?? 'Member'}</span>
                            {c.author?.role === 'admin' ? (
                              <Badge variant="info" className="text-[10px]">
                                Admin
                              </Badge>
                            ) : null}
                            <span>· {relativeTime(c.created_at)}</span>
                          </div>
                          {c.status !== 'deleted' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              title="Delete comment"
                              disabled={removeComment.isPending}
                              onClick={() => setRemoveCommentId(c.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          ) : null}
                        </div>
                        <p className="mt-1 whitespace-pre-line break-words text-vt-13 text-vt-text">
                          {c.status === 'deleted' ? '[deleted]' : c.body}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDrillId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hide-reason dialog */}
      <Dialog open={Boolean(hideTarget)} onOpenChange={(v) => (v ? null : setHideTarget(null))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Hide post</DialogTitle>
            <DialogDescription>
              The post stops appearing in the resident feed. You can restore it any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-2">
            <label htmlFor="hide-reason" className="text-vt-13 font-medium text-vt-text">
              Reason (optional)
            </label>
            <textarea
              id="hide-reason"
              rows={3}
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
              placeholder="Off-topic / harassment / spam …"
              className="w-full rounded-vt-md border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 text-vt-text focus:border-vt-primary focus:outline-none"
              maxLength={200}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHideTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={hide.isPending}
              onClick={() => {
                if (hideTarget) hide.mutate({ id: hideTarget.id, reason: hideReason });
              }}
            >
              Hide post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removePostTarget)}
        onOpenChange={(v) => (v ? null : setRemovePostTarget(null))}
        title="Delete post"
        description="Delete this post? Residents lose access to it immediately. This action cannot be undone."
        confirmLabel="Delete post"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removePostTarget) return;
          remove.mutate(removePostTarget.id, { onSuccess: () => setRemovePostTarget(null) });
        }}
      />

      <ConfirmDialog
        open={Boolean(removeCommentId)}
        onOpenChange={(v) => (v ? null : setRemoveCommentId(null))}
        title="Delete comment"
        description="Delete this comment from the post? The author will see it as removed."
        confirmLabel="Delete comment"
        variant="danger"
        loading={removeComment.isPending}
        onConfirm={() => {
          if (!removeCommentId) return;
          removeComment.mutate(removeCommentId, { onSuccess: () => setRemoveCommentId(null) });
        }}
      />
    </>
  );
}

function KpiCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-vt-4">
        <div>
          <div className="text-vt-12 font-medium uppercase tracking-wide text-vt-text-muted">{label}</div>
          <div className="mt-1 text-vt-24 font-semibold text-vt-text">{value}</div>
        </div>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-vt-primary-100 text-vt-primary">
          {icon}
        </span>
      </CardContent>
    </Card>
  );
}
